import {isPrimaryPersonNodeData} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramPrimaryPersonHelper";

export const LEGEND_HOVER_MATCH_CLASS = "legend-hover-match"
export const LEGEND_HOVER_DIM_CLASS = "legend-hover-dim"
export const LEGEND_INTERACTION_STATE_SCRATCH_KEY = "_qwLegendInteractionState"
export const LEGEND_HOVER_KIND = {
	LINE_SUB_TYPE: "lineSubType",
	HEALTH_CONDITION: "healthCondition",
	GROUP: "group",
	SYMBOL_TYPE: "symbolType",
	SEXUAL_COMMUNICATION: "sexualCommunication",
	PRIMARY_PERSON: "primaryPerson",
	PASSED_AWAY: "passedAway",
	PASSED_AWAY_PREGNANCY: "passedAwayPregnancy",
}

export function clearLegendHover(cy) {
	if(!cy){
		return
	}

	setLegendInteractionState(cy, {})
	cy.batch(() => {
		cy.elements(`.${LEGEND_HOVER_MATCH_CLASS}`).removeClass(LEGEND_HOVER_MATCH_CLASS)
		cy.elements(`.${LEGEND_HOVER_DIM_CLASS}`).removeClass(LEGEND_HOVER_DIM_CLASS)
	})
}

export function applyLegendHover(cy, hoverItem) {
	return applyLegendInteractionState(cy, {
		hoveredItem: hoverItem,
		lockedItems: [],
	})
}

export function applyLegendInteractionState(cy, state = {}) {
	if(!cy){
		return
	}

	clearLegendHover(cy)
	setLegendInteractionState(cy, state)
	let matchedElements = getLegendInteractionMatchElements(cy, state)
	if(!matchedElements.length){
		return
	}

	cy.batch(() => {
		cy.elements().difference(matchedElements).addClass(LEGEND_HOVER_DIM_CLASS)
		matchedElements.addClass(LEGEND_HOVER_MATCH_CLASS)
	})
}

export function getLegendHoverItemKey(hoverItem) {
	if(!hoverItem){
		return ""
	}

	return JSON.stringify(hoverItem)
}

export function getLegendInteractionMatchElements(cy, state = {}) {
	let activeItems = getLegendInteractionActiveItems(state)
	if(!activeItems.length){
		return cy.collection()
	}

	return getUnitedLegendHoverElements(cy, activeItems)
}

export function getLegendInteractionState(cy) {
	if(!cy){
		return {hoveredItem: null, lockedItems: [], activeItems: []}
	}

	let state = cy.scratch(LEGEND_INTERACTION_STATE_SCRATCH_KEY) || {}
	return normalizeLegendInteractionState(state)
}

function setLegendInteractionState(cy, state = {}) {
	cy.scratch(LEGEND_INTERACTION_STATE_SCRATCH_KEY, normalizeLegendInteractionState(state))
}

function normalizeLegendInteractionState(state = {}) {
	let hoveredItem = state.hoveredItem || null
	let lockedItems = Array.isArray(state.lockedItems) ? state.lockedItems.filter(Boolean) : []
	return {
		hoveredItem,
		lockedItems,
		activeItems: getLegendInteractionActiveItems({
			hoveredItem,
			lockedItems,
		}),
	}
}

function getLegendInteractionActiveItems(state = {}) {
	let lockedItems = Array.isArray(state.lockedItems) ? state.lockedItems.filter(Boolean) : []
	let hoveredItems = state.hoveredItem ? [state.hoveredItem] : []
	return [...lockedItems, ...hoveredItems]
}

function getUnitedLegendHoverElements(cy, hoverItems = []) {
	let matchedCollections = hoverItems.map((hoverItem) => getLegendHoverElements(cy, hoverItem)).filter((collection) => collection.length)
	if(!matchedCollections.length){
		return cy.collection()
	}

	let firstCollection = matchedCollections[0]
	return matchedCollections.slice(1).reduce((result, collection) => result.union(collection), firstCollection)
}

function getLegendHoverElements(cy, hoverItem) {
	switch (hoverItem.kind) {
		case LEGEND_HOVER_KIND.LINE_SUB_TYPE:
			return getLineSubTypeHoverElements(cy, hoverItem)
		case LEGEND_HOVER_KIND.HEALTH_CONDITION:
			return cy.nodes().filter((node) => hasMatchingHealthCondition(node, hoverItem))
		case LEGEND_HOVER_KIND.GROUP:
			return cy.nodes().filter((node) => hoverItem.nodeIds.includes(node.id()))
		case LEGEND_HOVER_KIND.SYMBOL_TYPE:
			return cy.nodes().filter((node) => isMatchingSymbolTypeNode(node, hoverItem.symbolType))
		case LEGEND_HOVER_KIND.SEXUAL_COMMUNICATION:
			return cy.nodes().filter((node) => isMatchingSexualCommunicationNode(node, hoverItem.sexualCommunication))
		case LEGEND_HOVER_KIND.PRIMARY_PERSON:
			return cy.nodes().filter((node) => isPrimaryPersonNodeData(node.data()))
		case LEGEND_HOVER_KIND.PASSED_AWAY:
			return cy.nodes().filter((node) => isPassedAwayNode(node) && node.data("type") !== "triangle")
		case LEGEND_HOVER_KIND.PASSED_AWAY_PREGNANCY:
			return cy.nodes().filter((node) => isPassedAwayNode(node) && node.data("type") === "triangle")
	}

	return cy.collection()
}

function getLineSubTypeHoverElements(cy, hoverItem) {
	let matchedEdges = cy.edges().filter((edge) => edge.data("lineSubType") === hoverItem.lineSubType)
	if(!matchedEdges.length){
		return cy.collection()
	}

	let matchedNodes = matchedEdges.connectedNodes().filter((node) => !isExcludedNode(node))
	return matchedEdges.union(matchedNodes)
}

function isMatchingSymbolTypeNode(node, symbolType) {
	if(isExcludedNode(node) || node.data("type") !== symbolType){
		return false
	}

	return !(symbolType === "triangle" && node.data("isPassedAway"))
}

function hasMatchingHealthCondition(node, hoverItem) {
	if(isExcludedNode(node)){
		return false
	}

	let tags = node.data("tags")
	if(!Array.isArray(tags)){
		return false
	}

	return tags.some((tag) => tag?.title === hoverItem.title && tag?.color === hoverItem.color)
}

function isMatchingSexualCommunicationNode(node, sexualCommunication) {
	if(isExcludedNode(node)){
		return false
	}

	return node.data("sexualCommunication") === sexualCommunication
}

function isPassedAwayNode(node) {
	return !isExcludedNode(node) && !!node.data("isPassedAway")
}

function isExcludedNode(node) {
	return !!node.data("isAuxNode") || !!node.data("excludeFromLegend")
}
